import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SessionService } from '../session.service';
import { APP_LANGUAGE_OPTIONS, AppLanguageCode, getLanguageOption } from '../i18n/language.config';
import { HeaderMenuService } from './header-menu.service';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './language-switcher.component.html',
  styleUrl: './language-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageSwitcherComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  protected readonly headerMenu = inject(HeaderMenuService);
  protected readonly session = inject(SessionService);
  protected readonly languages = APP_LANGUAGE_OPTIONS;
  protected readonly currentLanguage = computed(() => getLanguageOption(this.session.currentLanguage()));
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('languageTrigger');

  protected toggleMenu(): void {
    this.headerMenu.toggleLanguage();
  }

  protected selectLanguage(language: AppLanguageCode): void {
    this.session.setLanguage(language);
    this.headerMenu.closeLanguage();
    queueMicrotask(() => this.trigger()?.nativeElement.focus({ preventScroll: true }));
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.headerMenu.isLanguageOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) {
      return;
    }

    this.headerMenu.closeLanguage();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    if (!this.headerMenu.isLanguageOpen()) {
      return;
    }

    event.preventDefault();
    this.headerMenu.closeLanguage();
    queueMicrotask(() => this.trigger()?.nativeElement.focus({ preventScroll: true }));
  }
}
